// Push notification subscription helper.
//
// Responsibilities:
//   1. Detect platform (Capacitor native vs web/PWA).
//   2. On native Android: use @capacitor/push-notifications to get an FCM
//      token via the OS Firebase SDK.
//   3. On web/PWA: register firebase-messaging-sw.js, request browser
//      notification permission, getToken() against the Firebase JS SDK.
//   4. POST the token to `/api/push/subscribe` so the backend can fan out.
//   5. Wire foreground listener to play the custom MP3 sound when a push
//      arrives while the app is open (background pushes use OS sound).
//
// Failure modes are all silent — the existing in-app polling continues to
// work even if FCM is misconfigured.

import { firebaseEnabled, VAPID_KEY, ensureMessaging, getToken } from "./firebase";
import { API } from "../App";

// Lazy <audio> for the foreground chime. Same file used by AdminLayout.
// iOS Safari blocks Audio.play() unless triggered by a user gesture, so we
// pre-load + "unlock" the element on the first user interaction (any tap
// or click anywhere on the page). After the unlock, subsequent .play()
// calls from event handlers (e.g. FCM onMessage) work without being
// blocked by Safari's autoplay policy.
let _chime = null;
let _audioUnlocked = false;

function ensureChime() {
  if (!_chime) {
    _chime = new Audio("/sounds/new_order.mp3");
    _chime.preload = "auto";
    _chime.volume = 0.9;
  }
  return _chime;
}

function unlockAudioOnFirstGesture() {
  if (_audioUnlocked) return;
  const handler = async () => {
    if (_audioUnlocked) return;
    try {
      const a = ensureChime();
      a.muted = true;
      const p = a.play();
      if (p && typeof p.then === "function") {
        await p.catch(() => {});
      }
      a.pause();
      a.currentTime = 0;
      a.muted = false;
      _audioUnlocked = true;
    } catch { /* ignore */ }
    document.removeEventListener("click", handler);
    document.removeEventListener("touchstart", handler);
    document.removeEventListener("keydown", handler);
  };
  document.addEventListener("click", handler, { once: false, passive: true });
  document.addEventListener("touchstart", handler, { once: false, passive: true });
  document.addEventListener("keydown", handler, { once: false, passive: true });
}

// Install the unlock listener immediately on module import — safe in browsers
// only, harmless in Capacitor native (DOM exists).
if (typeof document !== "undefined") unlockAudioOnFirstGesture();

function playChime() {
  try {
    const a = ensureChime();
    a.currentTime = 0;
    const p = a.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  } catch { /* autoplay blocked or missing file */ }
}

export { playChime };

// Listen for foreground push messages forwarded by our service worker.
// When a push arrives while the PWA window is focused, the SW skips its
// own showNotification and `postMessage`s here instead — letting us play
// the custom MP3 chime. This is the only way to get a custom sound on
// iOS PWA foreground (Apple's WebKit always uses the device default
// for SW-shown notifications).
if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
  navigator.serviceWorker.addEventListener("message", (event) => {
    const d = event?.data;
    if (!d || d.type !== "fcm-foreground") return;
    playChime();
  });
}

async function postToken(token, platform) {
  try {
    await fetch(`${API}/push/subscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ token, platform }),
    });
  } catch (e) {
    console.warn("Push subscribe failed:", e);
  }
}

function isCapacitorNative() {
  // Capacitor injects window.Capacitor when running inside the native shell.
  // window.Capacitor.isNativePlatform() returns true on Android/iOS native.
  try {
    // eslint-disable-next-line no-undef
    return Boolean(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
  } catch {
    return false;
  }
}

async function setupCapacitorPush() {
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const perm = await PushNotifications.checkPermissions();
    if (perm.receive !== "granted") {
      const req = await PushNotifications.requestPermissions();
      if (req.receive !== "granted") return { ok: false, reason: "permission-denied" };
    }
    // Create the custom-sound notification channel BEFORE register().
    // Android 8+ requires channels to exist with the custom sound configured
    // upfront — once created, the channel's sound is immutable. We bumped
    // the id to `new_orders_v2` so any prior install picks up the new sound
    // after a reinstall / data clear.
    try {
      await PushNotifications.createChannel({
        id: "new_orders_v3",
        name: "New Orders",
        description: "Alerts for new incoming orders",
        sound: "new_order.ogg", // file in android/app/src/main/res/raw/new_order.ogg
        importance: 5, // IMPORTANCE_HIGH — heads-up + sound on lock screen
        visibility: 1,
        lights: true,
        vibration: true,
      });
    } catch (chErr) {
      console.warn("[push] createChannel failed (will fall back to defaults):", chErr);
    }
    PushNotifications.addListener("registration", (token) => {
      postToken(token.value, "android");
    });
    PushNotifications.addListener("registrationError", (err) => {
      console.warn("Capacitor push registration error:", err);
    });
    PushNotifications.addListener("pushNotificationReceived", () => {
      playChime();
    });
    await PushNotifications.register();
    return { ok: true };
  } catch (e) {
    const code = e?.code || "";
    const msg = e?.message || String(e);
    console.error("[push] Capacitor push setup failed:", { code, msg, error: e });
    return { ok: false, reason: "error", code, message: msg, error: e };
  }
}

async function setupWebPush() {
  if (!firebaseEnabled) {
    console.info("Firebase Web Push not configured (env vars missing) — skipping.");
    return { ok: false, reason: "not-configured" };
  }
  if (typeof Notification === "undefined" || !("serviceWorker" in navigator)) {
    return { ok: false, reason: "unsupported" };
  }

  if (Notification.permission === "default") {
    const p = await Notification.requestPermission();
    if (p !== "granted") return { ok: false, reason: "permission-" + p };
  }
  if (Notification.permission !== "granted") {
    return { ok: false, reason: "permission-denied" };
  }

  try {
    const reg = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    const messaging = await ensureMessaging();
    if (!messaging) return { ok: false, reason: "messaging-init-failed" };
    const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: reg });
    if (!token) return { ok: false, reason: "no-token" };
    await postToken(token, "web");
    // Foreground sounds are driven by the SW posting "fcm-foreground"
    // messages back to this page (see module-bottom listener). FCM SDK's
    // onMessage no longer fires because our SW is intentionally not
    // Firebase-aware (raw push handler for iOS reliability).
    return { ok: true, token };
  } catch (e) {
    const code = e?.code || "";
    const msg = e?.message || String(e);
    // Log full details so we can diagnose in browser console.
    console.error("[push] Web Push setup failed:", { code, msg, error: e });
    return { ok: false, reason: "error", code, message: msg, error: e };
  }
}

// Idempotent: call after auth resolves for staff roles. Safe to call multiple
// times — token refresh writes go to the same (user_id, token) doc. Pass
// `{ force: true }` from user-initiated triggers (e.g. the profile "Enable"
// button) to bypass the once-per-session guard so we can re-attempt after
// the user unblocks notifications in browser settings.
let _setupStarted = false;
export async function setupStaffPushNotifications({ force = false } = {}) {
  if (_setupStarted && !force) return { ok: false, reason: "already-attempted" };
  _setupStarted = true;
  if (isCapacitorNative()) return await setupCapacitorPush();
  return await setupWebPush();
}
